import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from '@stencil/core';

import Bind from 'bind-decorator';
import { StyleCollection } from '../../types';
import { getAdditionalPaddingFromBorderRadii } from '../../utils/border-radius-additional-padding';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { getComputedBorderRadii } from '../../utils/get-border-radius';
import { hasSlotted } from '../../utils/slotted';
import { LocaleService } from '../zds-config/locale-service';
import { ForceInvalidType } from '../zds-input-text/force-invalid-type';

/*
 * This is an internal component for the 'shell' of an input component
 *
 * Among other things, it supports:
 * - Slots content-start/-end and input-start/-end
 * - States disabled, plaintext, readonly
 * - States focus, invalid
 * - Clear button
 * - Instant-save styling
 */

@Component({
	shadow: true,
	styleUrl: 'zds-input-content-container.scss',
	tag: 'zds-input-content-container',
})
export class ZdsInputContentContainer {
	contentWrapperElement: HTMLDivElement;
	inputWrapperElement: HTMLDivElement;
	intersectionObserver: IntersectionObserver;

	@Element() hostElement: HTMLElement;

	@Event() zdsAccept: EventEmitter;
	@Event() zdsClear: EventEmitter;
	@Event() zdsClick: EventEmitter<MouseEvent>;
	@Event() zdsReject: EventEmitter;
	@Event() zdsShouldFocus: EventEmitter;

	@Prop() clearable: boolean = false;
	@Prop() disabled: boolean = false;
	@Prop() fitHeight: boolean = false;
	@Prop() focused: boolean = false;
	@Prop() hasValue: boolean = false;
	@Prop() instantSave: boolean | 'no-border' = false;
	@Prop() invalidType: ForceInvalidType = ForceInvalidType.None;
	@Prop() noPointerEvents: boolean = false; // internal prop to make component unresponsive but not alter styling like disabled/readonly does, parent will handle the click events
	@Prop() type: string;
	@Prop() plaintext: boolean = false;
	@Prop() readonly: boolean = false;
	@Prop() silentErrors: boolean = false; // Internal prop used for hiding the errors

	@State() contentStartPaddingLeft: string;
	@State() contentEndPaddingRight: string;
	@State() inputWrapperStyle: StyleCollection;
	@State() isStyleReady: boolean = false;

	@Method()
	async updateStyles() {
		// Get normal padding
		const paddingX = parseInt(window.getComputedStyle(this.hostElement).getPropertyValue(`--zds-input-inset-x`));

		// Get computed border radii for content wrapper and input wrapper
		// If content-start/-end enabled then both that content and the input-wrapper lose border-radius on the appropriate side
		const contentWrapperBorderRadii = getComputedBorderRadii(this.contentWrapperElement);
		const inputWrapperBorderRadii = getComputedBorderRadii(this.inputWrapperElement);
		if (contentWrapperBorderRadii === null || inputWrapperBorderRadii === null) return;

		// Determine additional padding that the content wrapper and input wrapper should get from their border radius
		const contentWrapperAdditionalPadding = getAdditionalPaddingFromBorderRadii(contentWrapperBorderRadii);
		const inputWrapperAdditionalPadding = getAdditionalPaddingFromBorderRadii(inputWrapperBorderRadii);

		// Computed padding for the content-start/end
		this.contentStartPaddingLeft = `calc(${paddingX}px + ${contentWrapperAdditionalPadding.left}px)`;
		this.contentEndPaddingRight = `calc(${paddingX}px + ${contentWrapperAdditionalPadding.right}px)`;

		// Computed padding for the input wrapper
		if (this.plaintext) {
			this.inputWrapperStyle = undefined;
		} else {
			this.inputWrapperStyle = {
				paddingLeft: `calc(${paddingX}px + ${inputWrapperAdditionalPadding.left}px)`,
				paddingRight: `calc(${paddingX}px + ${inputWrapperAdditionalPadding.right}px)`,
			};
		}

		// Component is hidden until these style computations are finished
		this.isStyleReady = true;
	}

	@Watch('instantSave')
	onInstantSaveChange(newValue: boolean | string) {
		if (newValue === true || newValue === false || newValue === 'no-border') return;

		// For normal boolean types, Stencil gives valueless HTML attributes the value `true`
		// Since instantSave is not just a boolean type, we have to do this ourselves
		if (newValue === '' || newValue === 'true') this.instantSave = true;
		else this.instantSave = false;
	}

	connectedCallback(): void {
		// Needed to init styles when the input is appended in the DOM
		if (this.contentWrapperElement && this.inputWrapperElement) {
			this.initStylesIfNeeded();
		}
	}

	componentWillLoad(): void {
		// Force initial call of the instantSave watcher
		this.onInstantSaveChange(this.instantSave);
	}

	componentDidLoad() {
		this.initStylesIfNeeded();

		this.intersectionObserver = new IntersectionObserver(this.handleIntersection);
		this.intersectionObserver.observe(this.hostElement);
	}

	disconnectedCallback() {
		this.intersectionObserver?.disconnect();
	}

	getSlotNodeName(slotName: string) {
		let targetElement: Element | Node = this.hostElement.querySelector(`:scope > [slot="${slotName}"]`);
		if (!targetElement) return null;

		while (targetElement.nodeName.toLowerCase() === 'slot') {
			// The slotted element is a slot itself, so return the name of its first slotted node
			const subNodes = (targetElement as HTMLSlotElement).assignedNodes();
			if (subNodes.length === 0) return null;
			targetElement = subNodes[0];
		}

		const nodeName = targetElement?.nodeName.toLowerCase();
		return nodeName;
	}

	@Bind
	handleIntersection() {
		this.initStylesIfNeeded();
	}

	initStylesIfNeeded() {
		if (this.isStyleReady) return;
		this.updateStyles();
	}

	@Bind
	onAcceptButtonClick() {
		this.zdsAccept.emit();
	}

	@Bind
	onClearButtonClick() {
		this.zdsClear.emit();
	}

	@Bind
	async onInputWrapperClick(event: MouseEvent) {
		this.zdsClick.emit(event);
		this.zdsShouldFocus.emit();
	}

	@Bind
	onRejectButtonClick() {
		this.zdsReject.emit();
	}

	render() {
		const contentWrapperClasses = generateCssClasses('zds-input-content-container', {
			disabled: this.disabled,
			fitHeight: this.fitHeight,
			focused: this.focused,
			invalid: this.invalidType !== ForceInvalidType.None,
			plaintext: this.plaintext,
			ready: this.isStyleReady,
		});

		const contentWrapperStyle = {
			'--zds-input-content-start-inset-left': this.contentStartPaddingLeft,
			'--zds-input-content-end-inset-right': this.contentEndPaddingRight,
		};

		const inputWrapperClasses = generateCssClasses('zds-input-content-container__input-wrapper', {
			disabled: this.disabled,
			focused: this.focused,
			hasContentStart: hasSlotted(this.hostElement, 'content-start'),
			hasContentEnd: hasSlotted(this.hostElement, 'content-end'),
			instantSave: this.instantSave !== false && !this.disabled && !this.readonly && !this.plaintext,
			instantSaveNoBorder:
				this.instantSave === 'no-border' && !this.disabled && !this.readonly && !this.plaintext,
			hidden: this.type === 'hidden',
			plaintext: this.plaintext,
			readonly: this.readonly,
		});

		const hostStyle = {
			height: this.fitHeight && '100%',
		};

		return (
			<Host style={hostStyle}>
				<div
					class={contentWrapperClasses}
					ref={(element) => (this.contentWrapperElement = element)}
					style={contentWrapperStyle}
				>
					{this.renderContentStart()}
					<div
						class={inputWrapperClasses}
						onClick={(event) => void this.onInputWrapperClick(event)}
						style={this.inputWrapperStyle}
						ref={(element) => (this.inputWrapperElement = element)}
					>
						{this.renderInputStart()}
						{this.renderInputSlot()}
						{this.renderInputEnd()}
					</div>
					{this.renderContentEnd()}
					{this.renderInstantSaveButtons()}
				</div>
			</Host>
		);
	}

	renderContentStart() {
		if (!hasSlotted(this.hostElement, 'content-start')) return null;

		const contentStartClasses = generateCssClasses('zds-input-content-container__content-start', {
			button: this.getSlotNodeName('content-start') === 'zds-button',
		});

		return (
			<div class={contentStartClasses}>
				<slot name="content-start"></slot>
			</div>
		);
	}

	renderContentEnd() {
		if (!hasSlotted(this.hostElement, 'content-end')) return null;

		const contentEndClasses = generateCssClasses('zds-input-content-container__content-end', {
			button: this.getSlotNodeName('content-end') === 'zds-button',
		});

		return (
			<div class={contentEndClasses}>
				<slot name="content-end"></slot>
			</div>
		);
	}

	renderInputEnd() {
		return (
			<div class="zds-input-content-container__input-end">
				{this.renderInstantSaveHoverIcon()}
				{this.renderClearButton()}
				{hasSlotted(this.hostElement, 'input-end') && <slot name="input-end"></slot>}
				{this.renderStatusIcon()}
			</div>
		);
	}

	renderInputSlot() {
		const inputClases = generateCssClasses(`zds-input-content-container__input`, {
			noEvents: this.noPointerEvents,
		});
		return (
			<div class={inputClases}>
				<slot name="input" />
			</div>
		);
	}

	renderInputStart() {
		if (!hasSlotted(this.hostElement, 'input-start')) return null;

		return (
			<div class="zds-input-content-container__input-start">
				<slot name="input-start"></slot>
			</div>
		);
	}

	renderInstantSaveButtons() {
		if (!this.instantSave || this.disabled || this.readonly || this.plaintext) return null;

		return (
			<div class="zds-input-content-container__instant-save-buttons">
				<button
					class="zds-input-content-container__instant-save-button zds-input-content-container__instant-save-button--reject"
					onMouseDown={this.onRejectButtonClick}
					onTouchStart={this.onRejectButtonClick}
					aria-label={LocaleService.t('zdsInputContentContainer.rejectLabel')}
				>
					<zds-icon name="xmark" library="fa-solid" size={4}></zds-icon>
				</button>
				<button
					class="zds-input-content-container__instant-save-button zds-input-content-container__instant-save-button--accept"
					onClick={this.onAcceptButtonClick}
					aria-label={LocaleService.t('zdsInputContentContainer.acceptLabel')}
				>
					<zds-icon name="check" library="fa-solid" size={4}></zds-icon>
				</button>
			</div>
		);
	}

	renderInstantSaveHoverIcon() {
		if (!this.instantSave || this.disabled || this.readonly || this.plaintext) return null;

		return (
			<zds-icon
				class="zds-input-content-container__instant-save-hover-icon"
				name="pen"
				library="fa-solid"
				size={4}
			/>
		);
	}

	renderClearButton() {
		if (!this.shouldRenderClearButton()) return null;

		return (
			<zds-button
				class="zds-input-content-container__clear-button"
				variant="text"
				preset="muted"
				size="sm"
				aspect-square
				pill
				overhang="right"
				onZdsClick={this.onClearButtonClick}
				inputAria={{ label: LocaleService.t('zdsInputContentContainer.clearLabel') }}
			>
				<zds-icon name="circle-xmark" library="fa-solid" size={4} />
			</zds-button>
		);
	}

	renderStatusIcon() {
		if (this.invalidType !== ForceInvalidType.WithStatusIcon || this.silentErrors) return null;

		return (
			<zds-icon
				class="zds-input-content-container__status-icon"
				name="circle-xmark"
				library="fa-solid"
				size={5}
			/>
		);
	}

	shouldRenderClearButton(): boolean {
		// Only show if clearable is enabled and the value is not empty
		if (!this.clearable || !this.hasValue) return false;

		// Never show if disabled, readonly or plaintext
		if (this.disabled || this.readonly || this.plaintext) return false;

		// Don't show if instantSave is enabled and there is no focus
		if (this.instantSave && !this.focused) return false;

		return true;
	}
}
